import {makeAutoObservable, observable, reaction} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {ModalType, RequestState} from "data/enums";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {IModalMessage, TModalArguments} from "data/types/modals";
import type {ITeamStore} from "data/stores/team/team.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";

export interface ISaveTeamModalController extends ViewController {
	get i18n(): ILocalizationStore;
	get visibleModal(): ModalType | null;
	get modalContent(): IModalMessage | null;

	close: () => void;
	show: (...args: TModalArguments) => void;
	onSaveTeam: () => void;
}

@injectable()
export class SaveTeamModalController implements ISaveTeamModalController {
	@observable _requestState: RequestState = RequestState.IDLE;
	private _disposerReaction!: ReturnType<typeof reaction>;

	get i18n() {
		return this._i18nStore;
	}

	get visibleModal() {
		return this._modalsStore.modal;
	}

	get modalContent() {
		return this._modalsStore.modalContent;
	}

	constructor(
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore
	) {
		makeAutoObservable(this);
	}

	show = (...args: TModalArguments) => {
		this._modalsStore.showModal(...args);
	};

	close = () => this._modalsStore.hideModal();

	onSaveTeam = () => {
		void this._teamStore.setTeam({
			lineup: this._teamStore.lineup,
			gamedayId: this._roundStore.selectedRoundId,
		});
	};

	dispose() {
		void this._disposerReaction;
	}

	init(param: void) {
		this._disposerReaction = reaction(
			() => this._teamStore.isTeamReadyForSave,
			(iReadyForSave) => {
				if (iReadyForSave) {
					this._modalsStore.showModal(ModalType.SAVE_TEAM, {
						message: "",
					});
				}
			},
			{fireImmediately: true}
		);
	}
}
